import { useEffect, useState } from "react";

import { Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";
import { OrganisationDetailsData, UpdateDetailsType } from "../../models";

interface UseAccessInvitesFormReturnData {
  allowInvitations: boolean;
  isSaving: boolean;
  handleChange: (allowInvitations: boolean) => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

export const useAccessInvitesForm = (
  objectUuid: string,
  objectKey: string,
  updateDetails: UpdateDetailsType,
  organisationDefaultDetails: OrganisationDetailsData,
  onDetailsUpdated: (details: OrganisationDetailsData) => void
): UseAccessInvitesFormReturnData => {
  const { hasPermission } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [allowInvitations, setAllowInvitations] = useState(!organisationDefaultDetails.accessInviteOptOut);

  // The details form patches the same record, so a save there is the source of truth here too.
  useEffect(() => {
    setAllowInvitations(!organisationDefaultDetails.accessInviteOptOut);
  }, [organisationDefaultDetails.accessInviteOptOut]);

  // The only write is the details patch, so the toggle resends the details as last loaded or saved.
  const handleChange = useIsLoadingWrapper(async (value: boolean): Promise<void> => {
    const accessInviteOptOut = !value;
    setAllowInvitations(value);

    const detailsRes = await updateDetails({
      [objectKey]: objectUuid,
      accessInviteOptOut,
      name: organisationDefaultDetails.displayName,
      displayName: organisationDefaultDetails.displayName,
      contactEmail: organisationDefaultDetails.contactEmail,
      contactPhone: organisationDefaultDetails.phoneNumber,
      socialMediaFacebookUrl: organisationDefaultDetails.facebookURL,
      socialMediaInstagramUsername: organisationDefaultDetails.instagramUsername,
      socialMediaLinkedInUrl: organisationDefaultDetails.linkedinURL,
      socialMediaTwitterUsername: organisationDefaultDetails.twitterUsername,
      socialMediaYoutubeChannel: organisationDefaultDetails.youtubeChannel,
      websiteUrl: organisationDefaultDetails.website,
      rowVersion: organisationDefaultDetails.rowVersion,
    });

    if (detailsRes.status === Status.Success && detailsRes.data) {
      Toast.success({ message: "Organisation details changed successfully" });

      onDetailsUpdated({
        ...organisationDefaultDetails,
        accessInviteOptOut,
        rowVersion: detailsRes.data.rowVersion,
      });
      return;
    }

    // Toggle holds its own checked state and only re-reads the prop when it changes, so the
    // restore has to move the value rather than leave it as it was before the click.
    setAllowInvitations(!organisationDefaultDetails.accessInviteOptOut);

    Toast.error({
      message: detailsRes.errors?.[0]?.message || "Couldn't save your project access invitation setting.",
    });
  }, setIsSaving);

  return {
    allowInvitations,
    isSaving,
    handleChange,
    hasPermission,
  };
};
